<template>
  <div class="w-full">
    <!-- 图例 -->
    <div v-if="showLegend && series.length" class="flex flex-wrap gap-x-4 gap-y-1.5 mb-2 px-1">
      <span
        v-for="(s, i) in series.filter((x) => x.points && x.points.length)"
        :key="'lg' + i"
        class="inline-flex items-center text-xs text-neutral-600"
      >
        <span
          class="inline-block w-4 h-0.5 mr-1.5 rounded"
          :style="{
            background: s.color,
            borderTop: s.dashed ? '2px dashed ' + s.color : 'none',
            height: s.dashed ? '0' : '2px',
          }"
        ></span>
        {{ s.name }}
      </span>
    </div>

    <svg :viewBox="`0 0 ${W} ${H}`" class="w-full" preserveAspectRatio="xMidYMid meet">
      <rect :x="padL" :y="padT" :width="plotW" :height="plotH" fill="#fafafa" rx="6" />

      <!-- y 网格与刻度 -->
      <g v-for="(t, i) in yTicks" :key="'y' + i">
        <line :x1="padL" :y1="t.y" :x2="padL + plotW" :y2="t.y" stroke="#e8e8e8" stroke-width="1" />
        <text :x="padL - 8" :y="t.y + 4" text-anchor="end" font-size="11" fill="#737373">
          {{ formatNum(t.value) }}
        </text>
      </g>

      <!-- x 网格与刻度 -->
      <g v-for="(p, i) in xTicks" :key="'x' + i">
        <line :x1="p.x" :y1="padT" :x2="p.x" :y2="padT + plotH" stroke="#f0f0f0" stroke-width="1" />
        <text :x="p.x" :y="padT + plotH + 18" text-anchor="middle" font-size="11" fill="#737373">
          {{ p.label }}
        </text>
      </g>

      <!-- 坐标轴 -->
      <line :x1="padL" :y1="padT" :x2="padL" :y2="padT + plotH" stroke="#d4d4d4" stroke-width="1" />
      <line
        :x1="padL"
        :y1="padT + plotH"
        :x2="padL + plotW"
        :y2="padT + plotH"
        stroke="#d4d4d4"
        stroke-width="1"
      />

      <text :x="padL + plotW / 2" :y="H - 4" text-anchor="middle" font-size="11" fill="#a3a3a3">
        {{ xTitle }}
      </text>
      <text
        :x="12"
        :y="padT + plotH / 2"
        text-anchor="middle"
        font-size="11"
        fill="#a3a3a3"
        :transform="`rotate(-90 12 ${padT + plotH / 2})`"
      >
        {{ yTitle }}
      </text>

      <!-- 各序列：预测虚线 + 折线 + 描点 -->
      <g v-for="(s, si) in series" :key="'s' + si">
        <template v-if="s.points && s.points.length">
          <line
            v-if="s.predict"
            :x1="scaleX(lastOf(s).x)"
            :y1="scaleY(lastOf(s).y)"
            :x2="scaleX(s.predict.x)"
            :y2="scaleY(s.predict.y)"
            :stroke="s.color"
            stroke-width="2"
            stroke-dasharray="5 4"
            opacity="0.7"
          />
          <path
            v-if="s.points.length > 1"
            :d="linePath(s)"
            fill="none"
            :stroke="s.color"
            :stroke-width="s.width || 2.5"
            :stroke-dasharray="s.dashed ? '6 4' : 'none'"
            stroke-linejoin="round"
            stroke-linecap="round"
            :opacity="s.dashed ? 0.95 : 1"
          />
          <g v-for="(p, pi) in s.points" :key="'p' + si + '-' + pi">
            <circle
              :cx="scaleX(Number(p.x))"
              :cy="scaleY(Number(p.y))"
              :r="s.dashed ? 3 : 3.6"
              :fill="s.dashed ? '#fff' : s.color"
              :stroke="s.color"
              stroke-width="1.6"
            />
            <text
              v-if="s.showValue && s.points.length <= 12"
              :x="scaleX(Number(p.x))"
              :y="scaleY(Number(p.y)) - 8"
              text-anchor="middle"
              font-size="10"
              :fill="s.color"
            >
              {{ formatNum(p.y) }}
            </text>
          </g>
          <g v-if="s.predict">
            <circle
              :cx="scaleX(s.predict.x)"
              :cy="scaleY(s.predict.y)"
              r="5"
              fill="#fff"
              :stroke="s.color"
              stroke-width="2.4"
              stroke-dasharray="3 2"
            />
            <text
              :x="scaleX(s.predict.x)"
              :y="scaleY(s.predict.y) - 11"
              text-anchor="middle"
              font-size="10"
              :fill="s.color"
              font-weight="bold"
            >
              {{ formatNum(s.predict.y) }}
            </text>
          </g>
        </template>
      </g>
    </svg>
  </div>
</template>

<script>
export default {
  name: "MultiLineChart",
  props: {
    // [{ name, color, points: [{x,y}], dashed, width, showValue, predict: {x,y} }]
    series: { type: Array, default: () => [] },
    xTitle: { type: String, default: "测量序号" },
    yTitle: { type: String, default: "温度 (℃)" },
    showLegend: { type: Boolean, default: true },
    // 横轴自定义标签，按 x 值取：xLabels[x]，如 ["组1","组2","组3"]
    xLabels: { type: Array, default: () => [] },
  },
  data() {
    return { W: 800, H: 320, padL: 56, padR: 24, padT: 26, padB: 44 };
  },
  computed: {
    plotW() {
      return this.W - this.padL - this.padR;
    },
    plotH() {
      return this.H - this.padT - this.padB;
    },
    allPoints() {
      const out = [];
      this.series.forEach((s) => {
        (s.points || []).forEach((p) => {
          if (p && isFinite(Number(p.y))) out.push({ x: Number(p.x), y: Number(p.y) });
        });
        if (s.predict && isFinite(Number(s.predict.y))) {
          out.push({ x: Number(s.predict.x), y: Number(s.predict.y) });
        }
      });
      return out;
    },
    xMin() {
      return this.allPoints.length ? Math.min(...this.allPoints.map((p) => p.x)) : 0;
    },
    xMax() {
      return this.allPoints.length ? Math.max(...this.allPoints.map((p) => p.x)) : 1;
    },
    yMin() {
      return this.allPoints.length ? Math.min(...this.allPoints.map((p) => p.y)) : 0;
    },
    yMax() {
      return this.allPoints.length ? Math.max(...this.allPoints.map((p) => p.y)) : 1;
    },
    yLo() {
      const span = this.yMax - this.yMin || 1;
      return this.yMin - span * 0.12;
    },
    yHi() {
      const span = this.yMax - this.yMin || 1;
      return this.yMax + span * 0.12;
    },
    yTicks() {
      // 纵坐标刻度取整：步长只用整数（1/2/5/10…），保证刻度标签没有小数
      const span = this.yHi - this.yLo;
      if (!(span > 0)) {
        return [];
      }
      const candidates = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000];
      let step = candidates[candidates.length - 1];
      for (let i = 0; i < candidates.length; i++) {
        if (span / candidates[i] <= 4) {
          step = candidates[i];
          break;
        }
      }
      const out = [];
      const start = Math.ceil(this.yLo / step) * step;
      for (let v = start; v <= this.yHi + 1e-9; v += step) {
        out.push({ value: Math.round(v), y: this.scaleY(v) });
      }
      return out;
    },
    xTicks() {
      const xs = Array.from(new Set(this.allPoints.map((p) => p.x))).sort((a, b) => a - b);
      const step = xs.length > 12 ? Math.ceil(xs.length / 10) : 1;
      const out = [];
      xs.forEach((x, i) => {
        if (i % step === 0 || i === xs.length - 1) {
          const label = this.xLabels[x] != null ? this.xLabels[x] : x;
          out.push({ x: this.scaleX(x), label });
        }
      });
      return out;
    },
  },
  methods: {
    scaleX(x) {
      const span = this.xMax - this.xMin;
      if (!span) return this.padL + this.plotW / 2;
      return this.padL + ((x - this.xMin) / span) * this.plotW;
    },
    scaleY(y) {
      const span = this.yHi - this.yLo;
      if (!span) return this.padT + this.plotH / 2;
      return this.padT + (1 - (y - this.yLo) / span) * this.plotH;
    },
    linePath(s) {
      return s.points
        .map(
          (p, i) =>
            `${i === 0 ? "M" : "L"} ${this.scaleX(Number(p.x))} ${this.scaleY(Number(p.y))}`
        )
        .join(" ");
    },
    lastOf(s) {
      return s.points[s.points.length - 1] || { x: 0, y: 0 };
    },
    formatNum(v) {
      const n = Number(v);
      if (isNaN(n)) return "-";
      return Math.abs(n) >= 100 || Number.isInteger(n)
        ? String(Math.round(n * 10) / 10)
        : String(Math.round(n * 100) / 100);
    },
  },
};
</script>
